import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import brand from '../theme/brand';
import AppHandoffSteps from './AppHandoffSteps';
import { createHandoffCode, HandoffCode } from '../services/AppHandoffService';

interface Props {
  open: boolean;
  onClose: () => void;
  formId: string;
  agentTitle: string;
}

/** Bottom sheet (phones) / dialog (computers): "Continue in the Paiper app". */
const AppHandoffSheet: React.FC<Props> = ({ open, onClose, formId, agentTitle }) => {
  const [handoff, setHandoff] = useState<HandoffCode | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try {
      setHandoff(await createHandoffCode(formId));
      setState('idle');
    } catch (error) {
      console.error('AppHandoffSheet: could not create code:', error);
      setState('error');
    }
  }, [formId]);

  // Ask on every open: the server hands back the same code while it's unused,
  // and a fresh one once the old code was redeemed or expired.
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          background: brand.creamPaper,
          color: brand.ink,
          borderRadius: '20px',
          m: 0,
          width: 'calc(100% - 32px)',
          position: 'relative',
          p: 3,
          maxHeight: 'calc(100% - 32px)',
          // bottom-anchored on phones
          '@media (max-width:599.95px)': {
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            width: 'auto',
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{ position: 'absolute', top: 10, right: 10, color: brand.warmGray }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography sx={{ fontFamily: brand.serif, fontSize: 24, color: brand.forest, pr: 4, lineHeight: 1.2 }}>
        Continue in the Paiper app
      </Typography>
      <Typography sx={{ fontSize: 14.5, color: brand.warmGray, mt: 0.5, mb: 2.5 }}>
        Your chat with {agentTitle} comes with you.
      </Typography>

      {state === 'loading' ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: brand.sage }} />
        </Box>
      ) : state === 'error' || !handoff ? (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography sx={{ mb: 1.5 }}>Couldn't get a code — try again</Typography>
          <Button variant="outlined" onClick={load} sx={{ textTransform: 'none', borderRadius: 999, borderColor: brand.sage, color: brand.forest }}>
            Try again
          </Button>
        </Box>
      ) : (
        <AppHandoffSteps code={handoff.code} url={handoff.url} />
      )}
    </Dialog>
  );
};

export default AppHandoffSheet;
